import { Shield, Users, Eye, Ear, Award, Target, Sparkles, ChevronRight, ArrowDown } from "lucide-react";
import { Link } from "react-router-dom";
import { JUNIOR_PROGRAM } from "../data/staticData";
import Reveal from "./Reveal";

const scrollToJuniorGuard = (e) => {
  e.preventDefault();
  const el = document.getElementById("junior-guard");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const HIGHLIGHTS = [
  { icon: Users, label: `Ages ${JUNIOR_PROGRAM.minAge}+` },
  { icon: Shield, label: "Parent / Guardian Required" },
  { icon: Target, label: "Safety-First Environment" },
  { icon: Sparkles, label: "Beginner Friendly" },
  { icon: Award, label: "Dry-Fire & Fundamentals" },
  { icon: Eye, label: "Eye Protection Required" },
  { icon: Ear, label: "Ear Protection Required" },
  { icon: Target, label: "Live-Fire Optional (per range)" },
  { icon: Award, label: "Junior Preparedness Certificate" },
];

export default function JuniorPreparedness() {
  return (
    <section
      id="junior-preparedness"
      data-testid="junior-preparedness-section"
      className="border-t border-white/10 py-20 bg-gradient-to-b from-[#0A0A0A] via-[#0F0F0F] to-[#0A0A0A] relative overflow-hidden"
    >
      {/* subtle grid backdrop */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(124,255,0,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(124,255,0,0.6) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      <div className="container-fld relative">
        <Reveal>
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-2 px-3 py-1 border border-[#7CFF00]/40 bg-[#7CFF00]/10 text-[#7CFF00] text-[10px] uppercase tracking-[0.2em] font-mono-tactical">
              <Sparkles className="w-3 h-3" /> Mentorship Program
            </span>
            <span className="overline">// Youth Preparedness</span>
          </div>

          <h2
            data-testid="junior-preparedness-title"
            className="font-tactical text-4xl md:text-6xl font-bold uppercase text-white tracking-tight leading-none max-w-4xl"
          >
            Junior Preparedness <span className="text-[#7CFF00]">Program</span>
          </h2>
          <div className="mt-5 flex flex-wrap items-center gap-4">
            <p className="max-w-3xl text-zinc-300 text-lg leading-relaxed">
              {JUNIOR_PROGRAM.description}
            </p>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <span
              data-testid="junior-preparedness-price"
              className="inline-flex items-baseline gap-1.5 px-4 py-2 border border-[#7CFF00] bg-black text-[#7CFF00] font-tactical uppercase tracking-widest shadow-[0_0_18px_rgba(124,255,0,0.35)]"
            >
              <span className="text-xs">{JUNIOR_PROGRAM.priceLabel}</span>
              <span className="text-2xl font-bold">${JUNIOR_PROGRAM.price}</span>
              <span className="text-[10px] text-[#7CFF00]/80">/ {JUNIOR_PROGRAM.priceUnit}</span>
            </span>
            <span className="font-mono-tactical text-[10px] text-zinc-500 uppercase tracking-widest">
              // {JUNIOR_PROGRAM.perLabel}
            </span>
          </div>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-12 gap-10 items-start">
          {/* Left content */}
          <Reveal delay={120} className="lg:col-span-7 space-y-6">
            <div className="tactical-card p-6 md:p-8">
              <div className="overline mb-3">// About the Program</div>
              <p className="text-zinc-300 leading-relaxed">
                Frontline Defense offers a youth preparedness and firearm safety
                mentorship experience designed to introduce young students to safe
                firearm handling, range etiquette, situational awareness, discipline,
                confidence-building, and responsible decision-making in a structured
                environment.
              </p>
              <p className="text-zinc-400 leading-relaxed mt-4">
                This is{" "}
                <span className="text-white font-semibold">
                  NOT a concealed carry certification course
                </span>{" "}
                and does not provide instructor credentials. Junior participants are
                guided alongside Frontline Defense staff in a safe, beginner-friendly
                setting focused on safety, respect, discipline, and personal growth.
              </p>
              <p className="text-zinc-300 leading-relaxed mt-4">
                Students who successfully complete the program may receive a{" "}
                <span className="text-[#7CFF00] font-semibold">
                  Frontline Defense Junior Preparedness Certificate
                </span>{" "}
                recognizing their commitment to safety, discipline, preparedness, and
                positive leadership qualities.
              </p>
            </div>

            {/* Junior Guard highlight note */}
            <div
              data-testid="junior-guard-note"
              className="relative border-l-2 border-[#7CFF00] bg-[#7CFF00]/[0.06] p-5 md:p-6"
            >
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-[#7CFF00] mt-0.5 flex-shrink-0" />
                <p className="text-zinc-200 text-sm md:text-base leading-relaxed">
                  Upon request, younger students may be accompanied by{" "}
                  <span className="text-[#7CFF00] font-semibold">
                    Frontline Defense Junior Guard (DJ)
                  </span>{" "}
                  for peer encouragement and beginner-friendly support during youth
                  sessions.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <Link
                to={`/booking?course=${encodeURIComponent(JUNIOR_PROGRAM.bookingValue)}`}
                data-testid="junior-preparedness-cta"
                className="btn-primary"
              >
                Enroll Your Junior <ChevronRight className="w-4 h-4" />
              </Link>
              <a
                href="#junior-guard"
                onClick={scrollToJuniorGuard}
                data-testid="meet-dj-tile"
                className="group inline-flex items-center gap-3 pl-2 pr-4 py-2 border border-[#7CFF00]/40 bg-black/40 hover:bg-[#7CFF00]/[0.08] hover:border-[#7CFF00] transition-colors"
              >
                <span className="relative w-10 h-10 rounded-full overflow-hidden border border-[#7CFF00] flex-shrink-0 shadow-[0_0_14px_rgba(124,255,0,0.4)]">
                  <img
                    src="/junior-guard-dj.jpg"
                    alt="DJ"
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="font-mono-tactical text-[9px] text-[#7CFF00] uppercase tracking-[0.22em]">
                    // Meet
                  </span>
                  <span className="font-tactical text-white uppercase text-sm tracking-widest group-hover:text-[#7CFF00] transition-colors">
                    Junior Guard (DJ)
                  </span>
                </span>
                <ArrowDown className="w-4 h-4 text-[#7CFF00] group-hover:translate-y-0.5 transition-transform" />
              </a>
              <span
                data-testid="junior-guard-badge"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#7CFF00] text-black text-[11px] uppercase tracking-[0.2em] font-tactical font-bold shadow-[0_0_24px_rgba(124,255,0,0.35)]"
              >
                <Shield className="w-3.5 h-3.5" />
                Junior Guard Available Upon Request
              </span>
            </div>
          </Reveal>

          {/* Right highlights grid */}
          <Reveal delay={200} className="lg:col-span-5">
            <div className="tactical-card p-6 md:p-8 corner-bracket">
              <div className="overline mb-5">// Program Highlights</div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {HIGHLIGHTS.map(({ icon: Icon, label }) => (
                  <li
                    key={label}
                    className="flex items-start gap-3 border border-white/10 hover:border-[#7CFF00]/40 transition-colors p-3 bg-black/30"
                  >
                    <Icon className="w-4 h-4 text-[#7CFF00] mt-0.5 flex-shrink-0" />
                    <span className="text-xs md:text-sm text-zinc-200 leading-snug">
                      {label}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-[11px] text-zinc-500 font-mono-tactical leading-relaxed">
                * Live-fire participation is optional and depends on the specific
                course schedule and range policies. Safety briefings and dry-fire
                fundamentals are always conducted first.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
